
import { Button } from "@/components/ui/button";
import { Grid, LayoutGrid, Menu } from "lucide-react";
import { motion } from "framer-motion";

export type ViewDensity = 'comfortable' | 'compact' | 'dense';

interface ViewDensityToggleProps {
  density: ViewDensity;
  onDensityChange: (density: ViewDensity) => void;
}

const ViewDensityToggle = ({ density, onDensityChange }: ViewDensityToggleProps) => {
  const densityOptions = [
    { value: 'comfortable' as const, icon: Menu, label: 'Comfortable' },
    { value: 'compact' as const, icon: LayoutGrid, label: 'Compact' },
    { value: 'dense' as const, icon: Grid, label: 'Dense' }
  ];

  return (
    <motion.div 
      className="flex items-center gap-1 bg-gradient-to-r from-muted/80 via-muted/60 to-muted/80 backdrop-blur-sm rounded-pill p-1 border border-border/50 shadow-card"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {densityOptions.map((option, index) => (
        <motion.div
          key={option.value}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1, duration: 0.2 }}
        >
          <Button
            variant={density === option.value ? "default" : "ghost"}
            size="sm"
            onClick={() => onDensityChange(option.value)}
            className={`h-8 px-3 rounded-pill transition-all duration-300 relative overflow-hidden group ${
              density === option.value 
                ? "bg-primary text-primary-foreground shadow-button scale-105" 
                : "hover:bg-white/20 hover:scale-105"
            }`}
          >
            {/* Active indicator animation */}
            {density === option.value && (
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              />
            )}
            
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="relative z-10 flex items-center gap-2"
            >
              <option.icon className="h-4 w-4" />
              <span className="hidden sm:inline font-medium">{option.label}</span>
            </motion.div>
            
            {/* Hover effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </Button>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default ViewDensityToggle;
